import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import useLogoUrl from "@/hooks/useLogoUrl";
import { siteData } from "@/store/siteData";

const DEFAULT_NAV_ITEMS = [
  { label: "Ana Sayfa", path: "/" },
  { label: "Hakkımızda", path: "/hakkimizda" },
  { label: "Ürünlerimiz", path: "/projelerimiz" },
  { label: "Stratejik Ortaklarımız", path: "/stratejik-ortaklarimiz" },
  { label: "İletişim", path: "/#iletisim" },
];

function buildNavItems() {
  const pages = siteData.getNavPages();
  if (pages.length > 0) {
    return [
      ...pages
        .filter(p => !p.hiddenFromNav)
        .map(p => ({ label: p.label, path: p.slug })),
      { label: "İletişim", path: "/#iletisim" },
    ];
  }
  return DEFAULT_NAV_ITEMS;
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [navItems, setNavItems] = useState(buildNavItems);
  const location = useLocation();
  const logoUrl = useLogoUrl();

  // Cache yüklenince ve her değişimde navItems'i güncelle
  const refreshNav = useCallback(() => {
    setNavItems(buildNavItems());
  }, []);

  useEffect(() => {
    // İlk yüklemede cache dolana kadar bekle, sonra güncelle
    siteData.load().then(() => refreshNav());
    // Cache değişimlerini dinle
    return siteData.subscribe(refreshNav);
  }, [refreshNav]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (path: string) => {
    setMobileOpen(false);
    if (path.includes("#")) {
      const id = path.split("#")[1];
      if (location.pathname !== "/") {
        window.location.href = path.startsWith("/") ? path : `/${path}`;
        return;
      }
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 bg-white border-b border-[#E2E8F0] ${
        scrolled ? "shadow-sm" : ""
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3">
          <img
            src={logoUrl}
            alt={siteData.getGeneralSettings().companyName}
            className="h-12 w-auto object-contain drop-shadow-lg"
          />
          <span className="hidden sm:flex flex-col leading-tight text-[#1E293B] font-light tracking-wide text-base">
            <span>Bilgi</span>
            <span>Teknolojileri</span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navItems.map((item) => (
            item.path.includes("#") ? (
              <button
                key={item.label}
                onClick={() => handleNavClick(item.path)}
                className={`text-sm font-medium tracking-wide transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                  location.pathname === item.path
                    ? "text-[#0F172A]"
                    : "text-[#475569] hover:text-[#0F172A]"
                }`}
              >
                {item.label}
              </button>
            ) : (
              <Link
                key={item.label}
                to={item.path}
                className={`text-sm font-medium tracking-wide transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                  location.pathname === item.path
                    ? "text-[#0F172A]"
                    : "text-[#475569] hover:text-[#0F172A]"
                }`}
              >
                {item.label}
              </Link>
            )
          ))}
        </div>

        {/* CTA Button */}
        <div className="hidden md:flex items-center">
          <button
            onClick={() => handleNavClick("/#iletisim")}
            className="whitespace-nowrap text-sm font-semibold tracking-widest px-6 py-2.5 border border-[#3B82F6] text-[#334155] hover:bg-[#3B82F6] hover:text-white transition-all duration-300 cursor-pointer"
          >
            İLETİŞİME GEÇ
          </button>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden w-8 h-8 flex items-center justify-center text-[#0F172A] cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Menü"
        >
          <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-3-line"} text-2xl`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-[#F1F5F9] border-t border-[#E2E8F0] px-6 py-6 flex flex-col gap-5">
          {navItems.map((item) => (
            item.path.includes("#") ? (
              <button
                key={item.label}
                onClick={() => handleNavClick(item.path)}
                className="text-left text-base text-[#475569] hover:text-[#0F172A] transition-colors cursor-pointer"
              >
                {item.label}
              </button>
            ) : (
              <Link
                key={item.label}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className="text-base text-[#475569] hover:text-[#0F172A] transition-colors cursor-pointer"
              >
                {item.label}
              </Link>
            )
          ))}
          <button
            onClick={() => handleNavClick("/#iletisim")}
            className="whitespace-nowrap mt-2 text-sm font-semibold tracking-widest px-6 py-3 border border-[#3B82F6] text-[#334155] hover:bg-[#3B82F6] hover:text-white transition-all duration-300 w-full text-center cursor-pointer"
          >
            İLETİŞİME GEÇ
          </button>
        </div>
      )}
    </nav>
  );
}
